import { WebGLRenderTarget, Vector2, Vector3, InstancedBufferAttribute, ShaderMaterial, BackSide, InstancedBufferGeometry, SphereGeometry } from 'three'
import * as THREE from 'three';
import React, { useMemo, useRef, useEffect, useState, useCallback } from 'react'
import { useFx, useSize, color} from '../fx.js';
import { Background } from '../bg.js';
import frag from './effect-balls-frag.js'
import vert from './effect-balls-vert.js'
import {CircleZone, PointZone, RestrictedZone} from '../zones.js';
import lerp from '@14islands/lerp'
const cameraFov = 50
const cameraZ = 40
const bgZ = -cameraZ*2
function Balls({backface = false, layers = 1}) {
	const fx = useFx()
	const size = useSize()
	const rendered = useRef(false)
	const type = fx.ballsType;
	const direction = fx.ballsDirection;
	const isNoSuspend = fx.bgAnimation === 'zoomInfinity'
	const speedMin = useMemo(()=>{
		const res = parseFloat(fx.ballsSpeedMin)
		return isNaN(res) ? 1 : res
	}, [fx.ballsSpeedMin])
	const speedMax = useMemo(()=>{
		const res = parseFloat(fx.ballsSpeedMax)
		return isNaN(res) ? 1.3 : res
	}, [fx.ballsSpeedMax])
	const pCount = useMemo(()=>{
		const res = parseInt(fx.ballsCount)
		return isNaN(res) || res < 1 ? 25 : res
	}, [fx.ballsCount])
	const pSize = useMemo(()=>{
		const res = parseFloat(fx.ballsSize)
		return isNaN(res) ? 5 : res
	}, [fx.ballsSize])
	const ior = useMemo(()=>{
		const res = parseFloat(fx.ballsIor)
		return isNaN(res) ? 1.2 : res
	}, [fx.ballsIor])
	const {camera, frameRun, gl, scene} = fx
	const lastTime = useRef(0);
	const [model, setModel] = useState(false)
	const [psGeometry, setPsGeometry] = useState(false)
	const [pGeometry, setPGeometry] = useState(false)
	const [backfaceFbo, setBackfaceFbo] = useState(false)
	const [backfaceMaterial, setBackfaceMaterial] = useState(false)
	const [envFbo, setEnvFbo] = useState(false)
	const [refractionMaterial, setRefractionMaterial] = useState(false)
	useEffect(() => {
		const envFbo = new WebGLRenderTarget(1, 1)
		setEnvFbo(envFbo)
		return () => {
			envFbo.dispose()
		}
	}, []);
	useEffect(() => {
		const refractionMaterialOpt = {
			defines: {},
			fragmentShader: frag,
			vertexShader: vert,
			uniforms: {
				uTime: { value: 0 },
				envMap: { value: null },
				resolution: { value: new Vector2(1, 1) },
				ior: { value: ior },
				reflectionColor: { value: new Vector3(0,0,0) }
			},
			depthTest: false,
			transparent: true
		}
		if(backface){
			refractionMaterialOpt.defines.USE_BACKFACE_MAP = ''
			refractionMaterialOpt.uniforms.backfaceMap = { value: null }
		}
		if(fx.reflectionColor){
			refractionMaterialOpt.defines.USE_FRESNEL = ''
		}
		const refractionMaterial = new THREE.ShaderMaterial(refractionMaterialOpt)
		setRefractionMaterial(refractionMaterial)
		return () => {
			refractionMaterial.dispose()
		}
	}, [fx.reflectionColor, backface, ior]);
	useEffect(() => {
		if(refractionMaterial && fx.reflectionColor !== undefined){
			refractionMaterial.uniforms.reflectionColor.value = color(fx.reflectionColor)
		}
	}, [fx.reflectionColor, refractionMaterial]);
	useEffect(() => {
		if(refractionMaterial && envFbo){
			refractionMaterial.uniforms.envMap.value = envFbo.texture
		}
	}, [refractionMaterial, envFbo]);

	useEffect(() => {
		let backfaceFbo
		let backfaceMaterial
		if(backface){
			backfaceFbo = new WebGLRenderTarget(size.width, size.height)
			backfaceMaterial = new ShaderMaterial({
				defines: {
					IS_BACKFACE: ''
				},
				fragmentShader: frag,
				vertexShader: vert,
				side: BackSide,
				uniforms: {
					uTime: { value: 0 }
				},
			})
		}else{
			backfaceFbo = false
			backfaceMaterial = false;
		}
		setBackfaceFbo(backfaceFbo)
		setBackfaceMaterial(backfaceMaterial)
		return () => {
			if(backfaceFbo){
				backfaceFbo.dispose()
				backfaceMaterial.dispose()
			}
		}
	}, [backface, size.height, size.width])
	useEffect(() => {
		if(backfaceFbo && refractionMaterial){
			refractionMaterial.uniforms.backfaceMap.value = backfaceFbo.texture;
		}
	}, [refractionMaterial, backfaceFbo]);

	useEffect(() => {
		if(refractionMaterial){
			let sourceZone;
			let targetZone;
			const dh = 15;
			const dw = dh * size.width/size.height;
			const dz = 20;
			const pGeometry = new SphereGeometry( pSize, 32, 32 )
			if(type==='vertical'){
				sourceZone = new RestrictedZone({x:[-dw, dw], y:[-dh, -dh], z:[-dz, dz]});
				targetZone = new RestrictedZone({x:[-dw, dw], y:[dh, dh], z:[-dz, dz]});
			}else if(type==='horizontal'){
				sourceZone = new RestrictedZone({x:[-dw, -dw], y:[-dh, dh], z:[-dz, dz]});
				targetZone = new RestrictedZone({x:[dw, dw], y:[-dh, dh], z:[-dz, dz]});
			}else{
				sourceZone = new PointZone(new Vector3(0, 0, 0))
				targetZone = new CircleZone(new THREE.Vector3(0, 0, 20), 10, 0, 0, true)
			}
			const psGeometry = new THREE.InstancedBufferGeometry()
			psGeometry.index = pGeometry.index;
			psGeometry.attributes = pGeometry.attributes;
			const pCount3 = pCount * 3;
			const sources = new Float32Array( pCount3 );
			const targets = new Float32Array( pCount3 );
			const durations = new Float32Array(pCount);
			const timeOffsets = new Float32Array(pCount);
			const durationMax = 40 / speedMin;
			const durationMin = 40 / speedMax;
			const dMax = Math.abs(durationMax);
			const dMin = dMax > durationMin ? durationMin : dMax;
			const durationDelta = dMax - dMin;
			for (let i = 0; i < pCount; i++) {
				if(pCount === 1){
					durations[i] = dMin + durationDelta / 2;
				}else{
					durations[i] = dMin + i * durationDelta / (pCount - 1);
				}
				if(
					direction === 'reverse' ||
					(direction === 'two' && i % 2 === 0)
				){
					durations[i] = -durations[i];
				}
				timeOffsets[i] = durations[i] * Math.random();
			}
			for (let i = 0, offset = 0; i < pCount; i++) {
				const point = sourceZone.createPoint();
				sources[offset++] = point.x;
				sources[offset++] = point.y;
				sources[offset++] = point.z;
			}
			for (let i = 0, offset = 0; i < pCount; i++) {
				const point = targetZone.createPoint();
				targets[offset++] = point.x;
				targets[offset++] = point.y;
				targets[offset++] = point.z;
			}
			psGeometry.setAttribute( 'aSource', new InstancedBufferAttribute( sources, 3 ) );
			psGeometry.setAttribute( 'aTarget', new InstancedBufferAttribute( targets, 3 ) );
			psGeometry.setAttribute( 'aDuration', new InstancedBufferAttribute( durations, 1 ) );
			psGeometry.setAttribute( 'aTimeOffset', new InstancedBufferAttribute( timeOffsets, 1 ) );
			const model = new THREE.Mesh(psGeometry, refractionMaterial)
			model.layers.set(layers)
			scene.add(model)
			setPGeometry(pGeometry)
			setPsGeometry(psGeometry)
			setModel(model)
			return () => {
				scene.remove(model)
				pGeometry.dispose()
				psGeometry.dispose()
			}
		}
	}, [pCount, type, size.width, size.height, direction, speedMin, speedMax, pSize, refractionMaterial, layers, scene]);

	useEffect(() => {
		const w = size.width;
		const h = size.height;
		if(refractionMaterial){
			refractionMaterial.uniforms.resolution.value.x = w;
			refractionMaterial.uniforms.resolution.value.y = h;
		}
		if(envFbo){
			envFbo.setSize(w, h)
		}
		if(backfaceFbo){
			backfaceFbo.setSize(w, h)
		}
		rendered.current = false;
	}, [size.width, size.height, refractionMaterial, backfaceFbo, envFbo])
	useEffect(()=>{
		if(model && envFbo && refractionMaterial){
			return frameRun((t)=>{
				if(
					(!isNoSuspend) && rendered.current && fx.isScrollOut()
				){
					lastTime.current = t;
					return;
				}
				rendered.current = true;
				let dt = t - lastTime.current;
				if(dt < 0){
					dt = 0
				}
				lastTime.current = t;
				refractionMaterial.uniforms.uTime.value += dt;
				model.rotation.x = lerp(model.rotation.x, (0.5 - fx.mouse.y), 0.05, dt)
				model.rotation.y = lerp(model.rotation.y, (fx.mouse.x - 0.5), 0.05, dt)
				camera.layers.set(0)
				gl.setRenderTarget(envFbo)
				gl.render(scene, camera)
				camera.layers.set(layers)
				if(backfaceFbo){
					backfaceMaterial.uniforms.uTime.value = refractionMaterial.uniforms.uTime.value
					model.material = backfaceMaterial
					gl.setRenderTarget(backfaceFbo)
					gl.render(scene, camera)
					model.material = refractionMaterial
				}
				camera.layers.enable(0)
				gl.setRenderTarget(null)
			})
		}
	}, [frameRun, model, envFbo, backfaceFbo, backfaceMaterial, refractionMaterial, fx, gl, layers, scene, camera, isNoSuspend])
	return null
}
const bgPosition = new Vector3(0,0,bgZ)
function App(){
	const fx = useFx();
	const bgReady = useCallback(()=>{
		fx.ready()
	},[fx])
	return(
		<>
			<Background position={bgPosition} readyFun={bgReady} />
			<Balls />
		</>
	)
}
const EffectBalls = {
	App,
	config: {
		createCamera: ()=>{
			const cam = new THREE.PerspectiveCamera(cameraFov, 1, 0.1, 1000)
			cam.position.z = cameraZ
			cam.lookAt(0, 0, 0)
			return cam
		}
	}
}
export {EffectBalls, Balls};
